import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Star, Grid3x3, FileText, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWorkspaceStore } from '../../store/workspace.store';
import { useAppTheme } from '../../hooks/useAppTheme';
export function MobileFavorites({ onClose }) {
    const navigate = useNavigate();
    const { isDark } = useAppTheme();
    const { currentWorkspace } = useWorkspaceStore();
    // Collect all starred items
    const starredItems = useMemo(() => {
        if (!currentWorkspace)
            return { boards: [], docs: [], dashboards: [], all: [] };
        const items = [];
        // Add starred boards
        (currentWorkspace.boards ?? []).filter(b => b.starred).forEach(board => {
            items.push({
                id: board.id,
                name: board.name,
                type: 'board',
                icon: Grid3x3,
                color: board.color,
                path: `/app/boards/${board.id}`
            });
        });
        // Add starred docs
        (currentWorkspace.docs ?? []).filter(d => d.starred).forEach(doc => {
            items.push({
                id: doc.id,
                name: doc.name,
                type: 'doc',
                icon: FileText,
                color: '#8B5CF6',
                path: `/app/ink/typst/${doc.ink_id}`
            });
        });
        // Add starred dashboards
        (currentWorkspace.dashboards ?? []).filter(d => d.starred).forEach(dashboard => {
            items.push({
                id: dashboard.id,
                name: dashboard.name,
                type: 'dashboard',
                icon: BarChart3,
                color: '#10B981',
                path: `/app/insights/dashboard/${dashboard.id}`
            });
        });
        // Organize by type
        return {
            boards: items.filter(i => i.type === 'board'),
            docs: items.filter(i => i.type === 'doc'),
            dashboards: items.filter(i => i.type === 'dashboard'),
            all: items
        };
    }, [currentWorkspace]);
    const handleItemClick = (path) => {
        navigate(path);
        onClose?.();
    };
    const renderItem = (item) => {
        const Icon = item.icon;
        return (_jsxs("button", { onClick: () => handleItemClick(item.path), className: "w-full flex items-center gap-3 px-3 py-2 rounded-lg border border-blue-500/30 bg-blue-500/10 hover:bg-blue-500/20 transition-all text-left", children: [_jsx(Icon, { className: "w-4 h-4 flex-shrink-0", style: { color: item.color || 'currentColor' } }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("p", { className: "text-sm font-medium text-adaptive-primary truncate", children: item.name }), _jsx("p", { className: "text-xs text-adaptive-muted capitalize", children: item.type })] }), _jsx(Star, { className: "w-4 h-4 text-yellow-400 fill-yellow-400 flex-shrink-0" })] }, item.id));
    };
    if (starredItems.all.length === 0) {
        return (_jsxs("div", { className: "py-12 text-center", children: [_jsx(Star, { className: "w-12 h-12 text-adaptive-muted/30 mx-auto mb-4" }), _jsx("p", { className: "text-sm text-adaptive-muted mb-2", children: "No Favorites Yet" }), _jsx("p", { className: "text-xs text-adaptive-muted/70", children: "Star items from Browse or Folders tab to add them here" })] }));
    }
    return (_jsxs("div", { className: "space-y-4", children: [_jsxs(motion.div, { initial: { opacity: 0 }, animate: { opacity: 1 }, className: "flex items-center gap-2 pb-4 border-b border-yellow-500/20", children: [_jsx(Star, { className: "w-5 h-5 text-yellow-400 fill-yellow-400" }), _jsx("span", { className: "text-sm font-semibold text-yellow-400", children: "Your Favorites" }), _jsxs("span", { className: "ml-auto text-xs text-adaptive-muted", children: [starredItems.all.length, " item", starredItems.all.length !== 1 ? 's' : ''] })] }), _jsx(motion.div, { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { delay: 0.1 }, children: _jsx("div", { className: "space-y-2", children: starredItems.all.map(item => renderItem(item)) }) }), _jsx(motion.div, { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { delay: 0.2 }, className: "mt-6 pt-6 border-t border-white/10", children: _jsxs("div", { className: "grid grid-cols-3 gap-3", children: [starredItems.boards.length > 0 && (_jsxs("div", { className: "px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-center", children: [_jsx(Grid3x3, { className: "w-4 h-4 mx-auto mb-1 text-adaptive-muted" }), _jsx("p", { className: "text-xs text-adaptive-muted", children: "Boards" }), _jsx("p", { className: "text-sm font-semibold text-adaptive-primary", children: starredItems.boards.length })] })), starredItems.docs.length > 0 && (_jsxs("div", { className: "px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-center", children: [_jsx(FileText, { className: "w-4 h-4 mx-auto mb-1 text-adaptive-muted" }), _jsx("p", { className: "text-xs text-adaptive-muted", children: "Docs" }), _jsx("p", { className: "text-sm font-semibold text-adaptive-primary", children: starredItems.docs.length })] })), starredItems.dashboards.length > 0 && (_jsxs("div", { className: "px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-center", children: [_jsx(BarChart3, { className: "w-4 h-4 mx-auto mb-1 text-adaptive-muted" }), _jsx("p", { className: "text-xs text-adaptive-muted", children: "Dashboards" }), _jsx("p", { className: "text-sm font-semibold text-adaptive-primary", children: starredItems.dashboards.length })] }))] }) })] }));
}
